import React, { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import Navbar from './components/Layout/Navbar';
import Footer from './components/Layout/Footer';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import GateScreen from './pages/GateScreen';
import { useAuthStore } from './store/authStore';
import DatosLayout, { DatosTheme } from './components/datos/DatosTheme';

// Páginas pesadas / menos frecuentes -> code splitting (mejor carga inicial)
const Projects = lazy(() => import('./pages/Projects'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Settings = lazy(() => import('./pages/Settings'));
const Comunidad = lazy(() => import('./pages/Comunidad'));
const Certificados = lazy(() => import('./pages/Certificados'));
const Certificacion = lazy(() => import('./pages/Certificacion'));
const Admin = lazy(() => import('./pages/Admin'));
const UploadProject = lazy(() => import('./pages/UploadProject'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const ModeloInteractivo = lazy(() => import('./pages/ModeloInteractivo'));
const TraducirProyectos = lazy(() => import('./pages/TraducirProyectos'));
const ComparteBase = lazy(() => import('./pages/ComparteBase'));
const SubmitRevision = lazy(() => import('./pages/SubmitRevision'));
const SubmitProject = lazy(() => import('./pages/SubmitProject'));
const TraducirIA = lazy(() => import('./pages/TraducirIA'));
const DataViewer = lazy(() => import('./pages/DataViewer'));
const DatosInicio = lazy(() => import('./pages/datos/DatosInicio'));
const DatosExplorer = lazy(() => import('./pages/datos/DatosExplorer'));
const DatosPreguntar = lazy(() => import('./pages/datos/DatosPreguntar'));
const DatosAdmin = lazy(() => import('./pages/datos/DatosAdmin'));
const DatosBase = lazy(() => import('./pages/datos/DatosBase'));
const DatosSubir = lazy(() => import('./pages/datos/DatosSubir'));
const DatosEmbed = lazy(() => import('./pages/datos/DatosEmbed'));
const DatosGuia = lazy(() => import('./pages/datos/DatosGuia'));

function NavigateToProfile() {
  const { username } = useParams();
  const clean = (username || '').replace(/^@/, '');
  return <Navigate to={clean ? `/profile/${clean}` : '/'} replace />;
}

function PageFallback() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
      <CircularProgress />
    </Box>
  );
}

function App() {
  const location = useLocation();
  const hydrate = useAuthStore((s) => s.hydrate);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hydrated = useAuthStore((s) => s.hydrated);
  const scope = useAuthStore((s) => s.user?.scope);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Sincroniza con la sesión del servidor al cargar (login con Google, rol/premium al día).
  useEffect(() => {
    hydrate();
  }, [hydrate]);

  // Gráficas insertables (<iframe> en artículos): públicas, sin muro de sesión, menú ni pie.
  if (location.pathname.startsWith('/embed/')) {
    return (
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/embed/datos/:slug" element={<DatosEmbed />} />
          <Route path="*" element={null} />
        </Routes>
      </Suspense>
    );
  }

  // Esperamos a confirmar la sesión con el servidor antes de decidir muro vs app
  // (evita parpadeo). Loader oscuro para no mostrar fondo blanco.
  if (!hydrated) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#06241d' }}>
        <CircularProgress sx={{ color: '#7ce0c0' }} />
      </Box>
    );
  }

  // BETA PRIVADA: sin sesión, todo queda tras el muro. Solo el gate misterioso y
  // las páginas de autenticación (para quienes ya fueron aprobados).
  if (!isAuthenticated) {
    return (
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/registro" element={<Register />} />
          <Route path="/verificar-email" element={<VerifyEmail />} />
          <Route path="/recuperar-contrasena" element={<ForgotPassword />} />
          <Route path="/restablecer-contrasena" element={<ResetPassword />} />
          <Route path="*" element={<GateScreen />} />
        </Routes>
      </Suspense>
    );
  }

  const datosRoutes = (
    <>
      <Route element={<DatosLayout />}>
        <Route path="/datos" element={<DatosInicio />} />
        <Route path="/datos/explorar" element={<DatosExplorer />} />
        <Route path="/datos/preguntar" element={<DatosPreguntar />} />
        <Route path="/datos/subir" element={<DatosSubir />} />
        <Route path="/datos/admin" element={<DatosAdmin />} />
        <Route path="/datos/guia" element={<DatosGuia />} />
        <Route path="/datos/:slug" element={<DatosBase />} />
      </Route>
    </>
  );

  // Cuenta con alcance 'datos' (p. ej. invitados de Redalyc): solo el módulo Datos, con su tema también en la barra y el pie.
  if (scope === 'datos') {
    return (
      <DatosTheme>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
          <Navbar />
          <Box component="main" sx={{ flexGrow: 1, pt: '70px' }}>
            <Suspense fallback={<PageFallback />}>
              <Routes>
                {datosRoutes}
                {/* certificación en blockchain (permiso «Certificación») y verificación pública de certificados */}
                <Route path="/certificacion" element={<Certificacion />} />
                <Route path="/certificados" element={<Certificados />} />
                <Route path="*" element={<Navigate to="/datos" replace />} />
              </Routes>
            </Suspense>
          </Box>
          <Footer minimal />
        </Box>
      </DatosTheme>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Navbar />
      <Box component="main" sx={{ flexGrow: 1, pt: '70px' }}>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/proyectos" element={<Projects />} />
            {datosRoutes}
            <Route path="/traducir-proyectos" element={<TraducirProyectos />} />
            <Route path="/proyecto/:slug" element={<ProjectDetail />} />
            <Route path="/proyecto/:slug/modelo" element={<ModeloInteractivo />} />
            <Route path="/proyecto/:slug/revision" element={<SubmitRevision />} />
            <Route path="/proyecto/:slug/datos" element={<DataViewer />} />
            <Route path="/comparte" element={<ComparteBase />} />
            <Route path="/subir-proyecto" element={<SubmitProject />} />
            <Route path="/traducir-ia" element={<TraducirIA />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verificar-email" element={<VerifyEmail />} />
            <Route path="/recuperar-contrasena" element={<ForgotPassword />} />
            <Route path="/restablecer-contrasena" element={<ResetPassword />} />
            <Route path="/comunidad" element={<Comunidad />} />
            {/* Rutas antiguas → redirigen a las nuevas (Búsqueda se fusionó en Proyectos; Ranking en Comunidad) */}
            <Route path="/busqueda" element={<Navigate to="/proyectos" replace />} />
            <Route path="/ranking" element={<Navigate to="/comunidad" replace />} />
            <Route path="/certificados" element={<Certificados />} />
            <Route path="/certificacion" element={<Certificacion />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/dashboard/settings" element={<Settings />} />
            <Route path="/profile/:username" element={<UserProfile />} />
            <Route path="/user/@:username" element={<NavigateToProfile />} />
            <Route path="/user/:username" element={<NavigateToProfile />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/subir" element={<UploadProject />} />
          </Routes>
        </Suspense>
      </Box>
      <Footer />
    </Box>
  );
}

export default App;
